import type { FuseResultMatch } from 'fuse.js';
import { ShareButton } from './ShareButton';
import { formatDuration } from '@/utils/format';

/**
 * Episode data structure for EpisodeCard
 * This is a presentational component that receives data as props
 */
export interface EpisodeCardData {
  readonly id: string;
  readonly episodeId: string;
  readonly topicSlug: string;
  readonly title: string;
  readonly summary: string;
  readonly publishedAt: string;
  readonly durationSeconds: number;
  readonly audioS3Key: string;
  readonly chapters: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly startTimeMs: number;
    readonly endTimeMs: number;
  }>;
  readonly isLikedByViewer?: boolean | null;
  readonly likeCount?: number;
}

interface EpisodeCardProps {
  episode: EpisodeCardData;
  isCurrentEpisode: boolean;
  isPlaying: boolean;
  onSelect: () => void;
  topicSlug: string;
  topicName: string;
  searchMatches?: readonly FuseResultMatch[];
  searchQuery?: string;
  showLikeButton?: boolean;
  onLike?: () => void;
  onUnlike?: () => void;
  isLikeMutationPending?: boolean;
}

export function EpisodeCard({
  episode,
  isCurrentEpisode,
  isPlaying,
  onSelect,
  topicSlug,
  topicName,
  searchMatches,
  searchQuery,
  showLikeButton,
  onLike,
  onUnlike,
  isLikeMutationPending,
}: EpisodeCardProps) {
  const formattedDate = new Date(episode.publishedAt).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );

  const formattedDuration = formatDuration(episode.durationSeconds);

  // Determine if title or summary has matches
  const titleMatch = searchMatches?.find((m) => m.key === 'title');
  const summaryMatch = searchMatches?.find((m) => m.key === 'summary');
  const hasSearchMatch = searchQuery && (titleMatch || summaryMatch);

  const isLiked = episode.isLikedByViewer === true;
  const likeCount = episode.likeCount ?? 0;

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLiked && onUnlike) {
      onUnlike();
    } else if (!isLiked && onLike) {
      onLike();
    }
  };

  return (
    <div
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      // biome-ignore lint/a11y/useSemanticElements: Card has nested interactive elements (share, like buttons)
      role="button"
      tabIndex={0}
      aria-label={`${isPlaying ? 'Pause' : 'Play'} episode: ${episode.title}`}
      className={`card w-full text-left transition-all hover:shadow-md cursor-pointer ${
        isCurrentEpisode ? 'ring-2 ring-sky-500' : ''
      } ${hasSearchMatch ? 'ring-1 ring-sky-300 dark:ring-sky-700' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
            {titleMatch ? (
              <HighlightedText text={episode.title} match={titleMatch} />
            ) : (
              episode.title
            )}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
            {summaryMatch ? (
              <HighlightedText text={episode.summary} match={summaryMatch} />
            ) : (
              episode.summary
            )}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
            <span>{formattedDate}</span>
            {episode.durationSeconds > 0 && (
              <>
                <span>•</span>
                <span>{formattedDuration}</span>
              </>
            )}
            <span>•</span>
            <ShareButton
              topicSlug={topicSlug}
              topicName={topicName}
              episodeId={episode.episodeId}
              episodeTitle={episode.title}
              variant="button"
              className="px-2 py-1"
            />
            {showLikeButton && (
              <>
                <span>•</span>
                <button
                  type="button"
                  onClick={handleLikeClick}
                  disabled={isLikeMutationPending}
                  className={`flex items-center gap-1 rounded px-2 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isLiked
                      ? 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300'
                      : 'text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400'
                  }`}
                  aria-label={`${isLiked ? 'Unlike' : 'Like'} ${episode.title}`}
                >
                  {isLikeMutationPending ? (
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4"
                      fill={isLiked ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth={isLiked ? 0 : 2}
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
                      />
                    </svg>
                  )}
                  {likeCount > 0 && <span>{likeCount}</span>}
                </button>
              </>
            )}
          </div>
        </div>
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
            isPlaying ? 'bg-sky-600' : 'bg-gray-100 dark:bg-gray-700'
          }`}
        >
          {isPlaying ? (
            <svg
              className="h-5 w-5 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-gray-600 dark:text-gray-300"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Component to highlight search matches within text
 */
function HighlightedText({
  text,
  match,
}: {
  text: string;
  match: FuseResultMatch;
}) {
  if (!match.indices || match.indices.length === 0) {
    return <>{text}</>;
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  // Sort indices by start position
  const sortedIndices = [...match.indices].sort((a, b) => a[0] - b[0]);

  for (const [start, end] of sortedIndices) {
    // Add text before the match
    if (start > lastIndex) {
      result.push(
        <span key={`text-${lastIndex}-${start}`}>
          {text.substring(lastIndex, start)}
        </span>
      );
    }

    // Add highlighted match
    result.push(
      <mark
        key={`match-${start}-${end}`}
        className="bg-yellow-200 text-gray-900 dark:bg-yellow-500 dark:text-gray-900 rounded px-0.5"
      >
        {text.substring(start, end + 1)}
      </mark>
    );

    lastIndex = end + 1;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    result.push(<span key="text-end">{text.substring(lastIndex)}</span>);
  }

  return <>{result}</>;
}
